import React, { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { Box, Grid, Typography } from '@trp-individual-investor-adv-svc/ii-prosp-components';

import {
    PsBeaconModal,
    PsBeaconControl,
    PsBeaconButton,
    PsBeaconLoadingSpinner,
} from '../shared/BeaconRegister';
import api from '../../actions/EndPoints';
import axiosInstance from '../../actions/AxiosInstance';
import { closeModal, commonErrorPromise, setLastRequestTime, showModal } from '../../Utils';
import { summaryCall } from '../../services/summaryService';
import { useClientServices } from '../clientServices';
import { isValidEmailAddress, testNameRegex } from '../shared/Validation';
import { USER_PROFILE_FORM_MODAL, PROSP_MODAL_PERSONAL_DETAILS } from '../../Constants';

const UserProfileForm = ({ state, dispatch}) => {
    const { userStatus, apiKey } = state;

    const [firstName, setFirstName] = React.useState(userStatus?.firstName);
    const [lastName, setLastName] = React.useState(userStatus?.lastName);
    const [email, setEmail] = React.useState(userStatus?.emailAddress);
    const [isLoading, setIsLoading] = React.useState(false);

    const FIELD_REQUIRED_ERR_MESSAGE = 'This field is required.';
    const specCharMess = `This field can only contain alphanumeric characters and/or the following special characters: -_.,/'&();`;
    const nameRegexString = "^[A-Za-z0-9\\-_.,\\/'&\\(\\);]+[ A-Za-z0-9\\-_.,\\/'&\\(\\);]*";
    const { putClientUserStatus, getKycInfo } = useClientServices(dispatch);
    const navigate = useNavigate();

    const redirectToConsent = (engagementType) => {
        if (!engagementType || engagementType === 'RAS-Onboarding') {
            navigate('/co-client-consent');
        } else {
            sessionStorage.setItem('joiningRasHH', true);
            navigate('/account-selection');
        }
    };

    const validateNameField = (value, controlId, fieldId, errorId) => {
        if (!value || !testNameRegex(value)) {
            const control = document?.querySelectorAll(`#${controlId}`)[0];
            const field = document?.querySelectorAll(`#${fieldId}`)[0];
            const errorSlot = control?.querySelectorAll(`#${errorId}`)[0];

            if (field?.validity?.valueMissing) {
                errorSlot.innerText = FIELD_REQUIRED_ERR_MESSAGE;
                control?.setAttribute('invalid', true);
            } else if (field?.validity?.patternMismatch) {
                errorSlot.innerText = specCharMess;
                control?.setAttribute('invalid', true);
            }
        }
    };

    const validateEmailField = () => {
        if (!email || !isValidEmailAddress(email)) {
            const control = document?.querySelectorAll(`#user-profile-email-control`)[0];
            const field = document?.querySelectorAll(`#user-profile-email-input-field`)[0];
            const errorSlot = control?.querySelectorAll(`#email-error`)[0];

            if (field?.validity?.valueMissing) {
                errorSlot.innerText = FIELD_REQUIRED_ERR_MESSAGE;
                control?.setAttribute('invalid', true);
            } else if (email && !isValidEmailAddress(email)) {
                errorSlot.innerText = 'This is not a valid email. Please try again.';
                control?.setAttribute('invalid', true);
            }
        }
    };

    const validateFields = () => {
        validateNameField(
            firstName,
            'user-profile-first-name-control',
            'user-profile-first-name-input-field',
            'first-name-error'
        );
        validateNameField(
            lastName,
            'user-profile-last-name-control',
            'user-profile-last-name-input-field',
            'last-name-error'
        );
        validateEmailField();
    };

    useEffect(() => {
        const getFirstNameControl = document?.querySelectorAll(
            `#user-profile-first-name-control`
        )[0];
        const getLastNameControl = document?.querySelectorAll(`#user-profile-last-name-control`)[0];
        const getEmailControl = document?.querySelectorAll(`#user-profile-email-control`)[0];

        if (firstName && testNameRegex(firstName)) {
            getFirstNameControl?.removeAttribute('invalid', true);
        }
        if (lastName && testNameRegex(lastName)) {
            getLastNameControl?.removeAttribute('invalid', true);
        }
        if (email && isValidEmailAddress(email)) {
            getEmailControl?.removeAttribute('invalid', true);
        }
    }, [firstName, lastName, email]);

    async function updateConsent() {
        try {
            const postConsent = await axiosInstance.post(
                api.consentAPI,
                { engagementType: 'RAS_ONBOARDING' },
                {
                    headers: {
                        apikey: `${apiKey}`,
                    },
                }
            );

            if (postConsent) {
                setLastRequestTime();
                dispatch({
                    type: 'CONSENT_UPDATED',
                    isConsented: true,
                });
                dispatch({
                    type: 'PROSP_STATUSES',
                    prospStatuses: ['NEWP'],
                });

                return postConsent;
            }
            return null;
        } catch (error) {
            commonErrorPromise(error);
            throw error;
        }
    }

    const handleOnClick = () => {
        validateFields();
        const headerPayload = {
            firstName: firstName,
            lastName: lastName,
            emailAddress: email,
        };
        if (
            email &&
            isValidEmailAddress(email) &&
            firstName &&
            testNameRegex(firstName) &&
            lastName &&
            testNameRegex(lastName)
        ) {
            // Co-client join intercept handling: PUT, close modal, navigate to consent
            if (state?.pinRecievedCoClientData) {
                setIsLoading(true);
                putClientUserStatus(apiKey, headerPayload)
                    .then(() => {
                        closeModal(USER_PROFILE_FORM_MODAL);
                        setIsLoading(false);
                        redirectToConsent(state?.pinRecievedCoClientData.engagementType);
                    })
                    .catch((err) => {
                        commonErrorPromise(err);
                        closeModal(USER_PROFILE_FORM_MODAL);
                        setIsLoading(false);
                    });
            } else {
                // Prosp intercept handling: PUT, updateConsent, summaryCall, show PI modal
                Promise.resolve(setIsLoading(true))
                    .then(() => putClientUserStatus(apiKey, headerPayload))
                    .then(() => updateConsent())
                    .then(() => summaryCall(apiKey, dispatch))
                    .then(() => getKycInfo(apiKey))
                    .then(() => showModal(PROSP_MODAL_PERSONAL_DETAILS))
                    .then(() => closeModal(USER_PROFILE_FORM_MODAL))
                    .then(() => setIsLoading(false))
                    .catch((err) => {
                        commonErrorPromise(err);
                        closeModal(USER_PROFILE_FORM_MODAL);
                        closeModal(PROSP_MODAL_PERSONAL_DETAILS);
                        setIsLoading(false);
                    });
            }
        }
    };

    return (
        <PsBeaconModal id={USER_PROFILE_FORM_MODAL} scale="jumbo">
            {!isLoading && (
                <Box className="beacon-theme-modern-outlook">
                    <Typography type="headline" serif loose scale={200}>
                        Update your user profile
                    </Typography>
                    <Typography
                        loose
                        sx={{ paddingBottom: '24px' }}>
                        To give you the best user experience, we need to ensure we have your correct
                        information on file.
                    </Typography>
                    <Grid id="user-profile-input-container" container>
                        <Grid id="user-profile-first-name-container" item cols={12} md={6}>
                            <PsBeaconControl
                                id="user-profile-first-name-control"
                                className="b-reset blocked_data"
                                style={{ backgroundColor: 'white' }}>
                                <label
                                    slot="label"
                                    id="first-name-label"
                                    htmlFor="user-profile-first-name-input-field">
                                    First name
                                </label>
                                <input
                                    type="text"
                                    id="user-profile-first-name-input-field"
                                    data-testid="first-name-input"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    pattern={nameRegexString}
                                    minLength="2"
                                    readOnly={userStatus?.firstName}
                                    disabled={userStatus?.firstName}
                                    required
                                />
                                <Box slot="error" id="first-name-error" />
                            </PsBeaconControl>
                        </Grid>
                        <Grid id="user-profile-last-name-container" item cols={12} md={6}>
                            <PsBeaconControl
                                id="user-profile-last-name-control"
                                className="b-reset blocked_data"
                                style={{ backgroundColor: 'white' }}>
                                <label
                                    slot="label"
                                    id="last-name-label"
                                    htmlFor="user-profile-last-name-input-field"
                                    className="b-typography-sans-serif--headline--50 b-font-weight-bold">
                                    Last name
                                </label>
                                <input
                                    type="text"
                                    id="user-profile-last-name-input-field"
                                    data-testid="last-name-input"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    pattern={nameRegexString}
                                    minLength="2"
                                    readOnly={userStatus?.lastName}
                                    disabled={userStatus?.lastName}
                                    required
                                />
                                <Box slot="error" id="last-name-error" />
                            </PsBeaconControl>
                        </Grid>
                        <Grid id="user-profile-email-container" item cols={12}>
                            <PsBeaconControl
                                id="user-profile-email-control"
                                className="b-reset blocked_data"
                                style={{ backgroundColor: 'white' }}>
                                <label
                                    slot="label"
                                    id="email-label"
                                    htmlFor="user-profile-email-input-field"
                                    className="b-typography-sans-serif--headline--50 b-font-weight-bold">
                                    Email address
                                </label>
                                <input
                                    type="email"
                                    className="b-reset"
                                    id="user-profile-email-input-field"
                                    data-testid="email-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    readOnly={userStatus?.emailAddress && userStatus?.emailVerified}
                                    disabled={userStatus?.emailAddress && userStatus?.emailVerified}
                                />
                                <Box slot="error" id="email-error" />
                            </PsBeaconControl>
                        </Grid>
                    </Grid>
                </Box>
            )}
            {isLoading && (
                <Box id="user-profile-loading-container">
                    <PsBeaconLoadingSpinner scale="normal" />
                    <Typography
                        type="headline" serif scale={300} bold
                        sx={{ paddingTop: '24px', paddingBottom: '24px' }}>
                        Updating your user profile information
                    </Typography>
                    <Typography type="headline" scale={100}>
                        Please wait while we update your user profile information...
                    </Typography>
                </Box>
            )}
            {!isLoading && (
                <Box
                    id="action-zone-container"
                    slot="action-zone"
                    className="fixed"
                    style={{ width: '100%' }}>
                    <Grid container sx={{ width: '100%', justifyContent: 'end' }}>
                        <Grid item cols={12} sx={{ justifyContent: { sm: 'end' } }}>
                            <PsBeaconButton
                                scale="jumbo"
                                id="user-profile-submit-button"
                                data-testid="beacon-button"
                                onClick={handleOnClick}
                                style={{ width: '100%' }}>
                                Submit
                            </PsBeaconButton>
                        </Grid>
                    </Grid>
                </Box>
            )}
        </PsBeaconModal>
    );
};

export default UserProfileForm;
